import { _ } from "js/vendor";

import { workspaces as workspacesApi } from "apis/callables";
import { WorkspaceUserRole } from "common/interfaces";
import { isCypress, isPPTAddin } from "js/config";
import getLogger, { LogGroup } from "js/core/logger";
import { auth } from "js/firebase/auth";
import { app } from "js/namespaces";
import { ShowSnackBar } from "js/react/components/Dialogs/SnackBar";

// Analytics providers
import { initAmplitude, trackAmplitude } from "js/analytics/providers/amplitude";
import { initRefiner, identifyRefiner, trackRefiner } from "js/analytics/providers/refiner";
import { identifyLogRocket } from "js/analytics/providers/logrocket";
import { identifyGTM, trackGTM } from "js/analytics/providers/gtm";
import { logAuditEvent, removePrefix } from "js/analytics/providers/audit";

const logger = getLogger(LogGroup.ANALYTICS);

// Default state
let state = {
    context: isPPTAddin ? "ppt" : "app",
    windowWidth: window.innerWidth,
    windowHeight: window.innerHeight
};

// tracking window size
const setWindowSize = () => {
    state.windowWidth = window.innerWidth;
    state.windowHeight = window.innerHeight;
};

window.addEventListener("resize", setWindowSize);

/**
 * Prepare event data for tracking
 */
function prepareEventData(eventId, options, props, role, user) {
    const sp_props = { ...props };
    removePrefix(sp_props, "sp_");

    const event = {
        event: eventId,
        eventCategory: options.category,
        eventAction: options.action,
        eventLabel: options.label,
        eventProperty: sp_props,
        eventValue: options.value,
        eventUserId: user ? user.uid : null,
        eventRole: role
    };

    const flushEvent = {
        event: "Flush",
        eventCategory: undefined,
        eventAction: undefined,
        eventLabel: undefined,
        eventProperty: undefined,
        eventValue: undefined,
        eventRole: undefined
    };

    return { event, flushEvent };
}

async function prepareIdentityData(user, plan) {
    const userInfo = app.user?.get("userInfo") || {};

    let workspaceMetadata = null;
    if (app.appController?.workspaceId) {
        const workspacesMetadata = await workspacesApi.getWorkspacesMetadata({ ids: [app.appController.workspaceId] });
        workspaceMetadata = workspacesMetadata[0];
    } else {
        workspaceMetadata = await workspacesApi.getDefaultWorkspaceMetadata();
    }

    const { id, subscriptionState, subscriptionStripeProductName, name } = workspaceMetadata;

    const userIdentity = {
        createdAt: user.metadata.creationTime,
        planTier: plan.tier,
        plan,
        stripePlanName: subscriptionStripeProductName,
        stripeSubscriptionStatus: subscriptionState?.subscriptionStatus,
        // Legacy
        organizationId: id,
        organizationName: name,
        // Current
        workspaceId: id,
        workspaceName: name,
        firstName: user.displayName,
        email: user.email,
        user_migrated: true,
        ...userInfo
    };

    const groupIdentity = {
        // Legacy
        organizationId: id,
        organizationName: name,
        // Current
        workspaceId: id,
        workspaceName: name,
        stripePlanName: subscriptionStripeProductName,
        stripeSubscriptionStatus: subscriptionState?.subscriptionStatus
    };

    return { workspaceMetadataId: id, userIdentity, groupIdentity };
}

export async function identify(user) {
    if (window.debugAnalytics) {
        logger.info("identify()", { uid: user.uid });
    }

    let plan = "N/A";

    let subscriptionStatus = null;
    if (app.user) {
        await app.user.load();
        const ownedWorkspace = app.user.workspacesMetadata
            .filter(workspace => workspace.userRole === WorkspaceUserRole.OWNER)
            .sort((a, b) => a.createdAt - b.createdAt)[0];
        if (ownedWorkspace) {
            plan = ownedWorkspace.plan.type;
            subscriptionStatus = ownedWorkspace.subscriptionState?.subscriptionStatus;
        }
    }

    // Initialize services BEFORE session_tracking_disabled check
    initAmplitude(user, trackState);
    initRefiner();
    identifyLogRocket(user, plan, subscriptionStatus);

    // Check if session tracking is disabled
    const { claims } = await user.getIdTokenResult();
    if (claims.session_tracking_disabled) {
        logger.info(`identify() ${user.uid} user has the "session_tracking_disabled" custom claim set to true, won't be tracking session`, { uid: user.uid });
        return;
    }

    const { workspaceMetadataId, userIdentity, groupIdentity } = await prepareIdentityData(user, plan);

    // Identify with services AFTER session_tracking_disabled check
    identifyGTM(user);
    await identifyRefiner(user, userIdentity);
}

/**
 * Update the state that is sent along with trackEvent().
 */
export function trackState(props) {
    const newState = _.merge({}, state, props);
    if (window.debugAnalytics && !_.isEqual(state, newState)) {
        logger.info("state", newState);
    }
    state = newState;
}

export function getState(propName = null) {
    return propName ? state[propName] : state;
}

/**
 * Helper to recursively update any state variables which appear in event props.
 */
function updateState(obj, updates, path = []) {
    for (const [key, value1] of Object.entries(obj)) {
        const childPath = [...path, key];
        const value2 = updates[key];
        if (value1 === undefined || value1 === null) {
            continue;
        }
        if (typeof value1 == "object") {
            updateState(value1, value2, childPath);
        } else {
            if (value1 === value2) {
                if (window.debugAnalytics) {
                    logger.warn(`[ANALYTICS] "${childPath.join(".")}" is redundant - tracked in state`);
                }
            } else if (updates.hasOwnProperty(key)) {
                if (window.debugAnalytics) {
                    logger.info("state", { update: childPath.join(".") + "->" + value2 });
                }
                obj[key] = value2;
            }
        }
    }
}

/**
 * Track an event across all analytics platforms.
 */
export async function track(options = {}) {
    const user = auth().currentUser;
    if (user) {
        const { claims } = await user.getIdTokenResult();
        if (claims.session_tracking_disabled) {
            logger.info(`track() ${user.uid} user has the "session_tracking_disabled" custom claim set to true, won't be tracking session`, { uid: user.uid });
            return;
        }
    }

    // merge state into props
    const props = _.merge({}, state, options.props);
    const role = props.role;

    const eventId = options.category + ":" + options.action;

    // update any state variables that are changed in props
    updateState(state, options.props);

    if (window.debugAnalytics && !isCypress) {
        logger.info("track", { ...options, eventId, props });

        // Delay showing the snackbar so that mounting works properly in player view.
        setTimeout(
            () => ShowSnackBar({
                message: `[TRACKING EVENT] ${eventId}`
            }),
            500
        );
    }

    if (!props.workspace_id) {
        logger.warn(`[ANALYTICS] options.props.workspace_id required in all events - ${eventId}`);
    }

    // Prepare event data
    const { event, flushEvent } = prepareEventData(eventId, options, props, role, user);

    // Log audit event if requested
    await logAuditEvent(options, props, user);

    // Track with all analytics platforms
    trackAmplitude(eventId, event);
    trackGTM(event, flushEvent);
    trackRefiner(eventId, event.eventProperty, user);
}

export function getDebugAnalyticsState() {
    return state;
}
